import React, { useState, useEffect } from "react";
import {
  Input,
  Descriptions,
  Row,
  Col,
  notification,
  Checkbox,
  Button,
  DatePicker,
} from "antd";

import {
  ProfileOutlined,
  BarcodeOutlined,
  TagOutlined,
  NumberOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";

import axiosInstance from "utilities/axios";
import dayjs from "dayjs";

import formatDate from "utilities/formats/formatDate";
import useRoleCheck from "utilities/hooks/roleCheck";
import DeleteConfirm from "./DeleteConfirm";

const DetailsTab = ({ equipment, equipmentLock, updateEquipment, onClose }) => {
  const { hasPermission } = useRoleCheck();
  const [condition, setCondition] = useState(
    equipment?.condition === "Working"
  );
  const [status, setStatus] = useState(equipment?.status === "In Use");
  const [serialNo, setSerialNo] = useState(equipment?.serialNo);
  const [notes, setNotes] = useState(equipment?.notes);
  const [purchaseDate, setPurchaseDate] = useState(equipment?.purchaseDate);

  const [prevStatus, setPrevStatus] = useState(status); // Store the previous status to restore later
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isDeleteModalVisible, setDeleteModalVisible] = useState(false);

  const handleConditionChange = (checked) => {
    setCondition(checked);
    if (!checked) {
      setPrevStatus(status); // remember the previous status
      setStatus(false); // turn off "in use"
    } else {
      setStatus(prevStatus); // restore previous status
    }
  };

  const saveChanges = () => {
    setLoading(true);
    axiosInstance
      .patch(`/equipment/inventory/${equipment.id}`, {
        condition: condition ? "Working" : "Not Working",
        status: status ? "In Use" : "Not In Use",
        notes: notes,
        serialNo: serialNo,
        purchaseDate: purchaseDate,
      })

      .then((response) => {
        updateEquipment();
        notification.success({
          message: "Success",
          description: "Equipment updated successfully!",
        });
        setEditMode(false);
      })
      .catch((error) => {
        notification.error({
          message: "Error",
          description: "Failed to update equipment!",
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    const resetFields = () => {
      setCondition(equipment?.condition === "Working");
      setStatus(equipment?.status === "In Use");
      setSerialNo(equipment?.serialNo);
      setNotes(equipment?.notes);
    };

    if (!editMode) {
      resetFields();
    }
  }, [editMode, equipment]);
  if (!equipment) {
    return null;
  }
  return (
    <>
      <Descriptions bordered column={1}>
        <Descriptions.Item
          label={
            <>
              <TagOutlined /> Brand
            </>
          }
        >
          {equipment.equipment.brand}
        </Descriptions.Item>
        <Descriptions.Item
          label={
            <>
              <BarcodeOutlined /> Model
            </>
          }
        >
          {equipment.equipment.model}
        </Descriptions.Item>
        <Descriptions.Item
          label={
            <>
              <ProfileOutlined /> Category
            </>
          }
        >
          {equipment.equipment.category}
        </Descriptions.Item>
      </Descriptions>
      <br />
      <Descriptions bordered column={4}>
        <Descriptions.Item
          span={3}
          label={
            <>
              <NumberOutlined /> Serial No
            </>
          }
        >
          {!editMode ? (
            equipment.serialNo
          ) : (
            <Input
              defaultValue={equipment.serialNo}
              onChange={(e) => setSerialNo(e.target.value)}
            />
          )}
        </Descriptions.Item>
        <Descriptions.Item span={2} label="Clinic No">
          {equipment.clinicNo}
        </Descriptions.Item>
        <Descriptions.Item span={2} label="Last Modified">
          {formatDate(equipment.modified.date)}
        </Descriptions.Item>
        <Descriptions.Item span={2} label="Modified By">
          {equipment.modified.firstName} {equipment.modified.lastName} (
          {equipment.modified.initials})
        </Descriptions.Item>

        <Descriptions.Item span={2} label="Purchase Date">
          {editMode && hasPermission(["Audiology"]) ? (
            <DatePicker
              defaultValue={
                equipment.purchaseDate && dayjs(equipment.purchaseDate)
              }
              onSelect={(date) => setPurchaseDate(date)}
            />
          ) : (
            formatDate(equipment.purchaseDate)
          )}
        </Descriptions.Item>
        <Descriptions.Item span={2} label="Added Date">
          {formatDate(equipment.dateAdded)}
        </Descriptions.Item>

        <Descriptions.Item label="Notes" span={4}>
          {!editMode ? (
            equipment.notes
          ) : (
            <Input
              defaultValue={equipment.notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          )}
        </Descriptions.Item>
      </Descriptions>
      <Row
        justify="space-between"
        align="middle"
        style={{ width: "100%", marginTop: 8 }}
      >
        <Col>
          <Checkbox
            checked={condition}
            onChange={(e) => handleConditionChange(e.target.checked)}
            disabled={!editMode}
          >
            Working
          </Checkbox>
          <Checkbox
            checked={status}
            onChange={(e) => setStatus(e.target.checked)}
            disabled={!condition || !editMode}
          >
            In Use
          </Checkbox>
        </Col>
        <Col>
          {editMode && (
            <>
              <Button
                key="save"
                type="primary"
                onClick={saveChanges}
                loading={loading}
                disabled={
                  editMode &&
                  condition === (equipment.condition === "Working") &&
                  status === (equipment.status === "In Use") &&
                  serialNo === equipment.serialNo &&
                  notes === equipment.notes &&
                  purchaseDate === equipment.purchaseDate
                }
                style={{ marginRight: 8 }}
              >
                Save Changes
              </Button>
              {hasPermission(["Audiology", "Sales.RM", "Sales.Exec"]) && (
                <Button
                  key="delete"
                  type="primary"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => setDeleteModalVisible(true)}
                />
              )}
            </>
          )}
          {hasPermission(
            ["Audiology"],
            ["Sales.Staff", "Sales.RM", "Sales.Exec"],
            equipment.clinicNo,
            false
          ) &&
            !equipmentLock && (
              <Button
                key="edit"
                type={editMode ? "dashed" : "text"}
                icon={<EditOutlined />}
                style={{ marginLeft: 8 }}
                onClick={() => setEditMode(!editMode)}
              />
            )}
        </Col>
      </Row>
      <DeleteConfirm
        id={equipment.id}
        open={isDeleteModalVisible}
        onClose={() => setDeleteModalVisible(false)}
        onDelete={() => {
          updateEquipment();
          onClose();
        }}
      />
    </>
  );
};

export default DetailsTab;
